import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Grid,
  GridItem,
  Label,
  MenuToggle,
  MenuToggleElement,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Split,
  SplitItem,
  Toolbar,
  ToolbarContent,
  ToolbarFilter,
  ToolbarGroup,
  ToolbarItem,
} from "@patternfly/react-core";
import PageHeader from '@patternfly/react-component-groups/dist/dynamic/PageHeader';
import useLogger from "src/hooks/useLogger";
import { Link } from "react-router-dom";
import { LiveKitViewerProvider } from "src/providers/LiveKitViewerProvider";
import BotControlModal from "./BotControlModal";
import { GlobeIcon, InfoCircleIcon, LightbulbIcon } from "@patternfly/react-icons";
import { useAuthedFetch } from "src/hooks/useAuthedFetch";
import { z } from "zod";
import {
  type ReadinessComponents,
  coerceReadinessComponents,
  deriveAggregateReadiness,
} from "src/app/utils/readiness";

interface Client {
  id: string;
  name: string;
  image: string;
  type?: string;
  telemetry: {
    ip: string;
    battery: number;
    teleop_status?: Record<string, unknown>;
    teleop_status_at?: string;
    room_id?: string | null;
    readiness?: boolean | null;
    availability?: boolean | null;
    readiness_components?: ReadinessComponents;
  };
  status: string;
  videoRef?: React.RefObject<HTMLVideoElement>;
}

const ClientEnvelopeSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  image: z.string().optional(),
  type: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  telemetry: z.record(z.unknown()).nullable().optional(),
}).passthrough();

const RobotListResponseSchema = z.array(
  ClientEnvelopeSchema.extend({
    serial_number: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
    location_id: z.string().nullable().optional(),
    last_heartbeat_at: z.string().nullable().optional(),
  })
);

const TelemetryStatusSchema = z.object({
  robot_id: z.string().nullish(),
  robot_name: z.string().nullish(),
  room_id: z.string().nullish(),
  teleop_status: z.record(z.unknown()).nullish(),
  teleop_status_at: z.string().nullish(),
  readiness_components: z.unknown().optional(),
}).passthrough();

const TelemetryStatusListSchema = z.array(TelemetryStatusSchema);

type ClientEnvelope = z.infer<typeof ClientEnvelopeSchema>;
type TelemetryStatus = z.infer<typeof TelemetryStatusSchema>;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DEFAULT_TRACK = 'robot_overhead';

const toBooleanIndicator = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'ready' || value.toLowerCase() === 'available') {
      return true;
    }
    if (value.toLowerCase() === 'not_ready' || value.toLowerCase() === 'unavailable') {
      return false;
    }
  }
  return null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value as Record<string, unknown>;
};

const normalizeTelemetry = (value: unknown): Client['telemetry'] => {
  const telemetry = asRecord(value) ?? {};
  const teleopStatus = asRecord(telemetry.teleop_status);
  const readinessComponents = coerceReadinessComponents(
    telemetry.readiness_components
  );
  const derivedReadiness = deriveAggregateReadiness(readinessComponents);
  const readiness =
    toBooleanIndicator(telemetry.readiness)
    ?? toBooleanIndicator(teleopStatus?.readiness)
    ?? derivedReadiness;
  const availability =
    toBooleanIndicator(telemetry.availability)
    ?? toBooleanIndicator(teleopStatus?.availability)
    ?? derivedReadiness;

  return {
    ip: typeof telemetry.ip === 'string' ? telemetry.ip : '—',
    battery: typeof telemetry.battery === 'number' ? telemetry.battery : 0,
    teleop_status: teleopStatus ?? undefined,
    teleop_status_at: typeof telemetry.teleop_status_at === 'string'
      ? telemetry.teleop_status_at
      : undefined,
    room_id: typeof telemetry.room_id === 'string' ? telemetry.room_id : null,
    readiness,
    availability,
    readiness_components: readinessComponents,
  };
};

const normalizeClient = (
  payload: ClientEnvelope,
  defaults: { status: string }
): Client => ({
  id: payload.id,
  name: payload.name || payload.id,
  image: payload.image || '',
  type: payload.type || 'robot',
  status: payload.status || defaults.status,
  telemetry: normalizeTelemetry(payload.telemetry),
});

const upsertClientsById = (current: Client[], incoming: Client[]): Client[] => {
  const clientsById = new Map(current.map((client) => [client.id, client]));
  for (const nextClient of incoming) {
    const existingClient = clientsById.get(nextClient.id);
    if (!existingClient) {
      clientsById.set(nextClient.id, nextClient);
      continue;
    }
    clientsById.set(nextClient.id, {
      ...existingClient,
      ...nextClient,
      telemetry: {
        ...existingClient.telemetry,
        ...nextClient.telemetry,
      },
    });
  }
  return Array.from(clientsById.values());
};

const StatusLight = ({ label, value }: { label: string; value: boolean | null }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
    <span
      style={{
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: value === true ? '#3e8635' : value === false ? '#c9190b' : '#8a8d90',
        display: 'inline-block',
      }}
    />
    {label}
  </span>
);

const Dashboard = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const { logError } = useLogger();
  const authedFetch = useAuthedFetch();

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Search and filter states
  const [searchText, setSearchText] = useState('');
  const [isStatusExpanded, setIsStatusExpanded] = React.useState(false);

  const [isBatterySelectOpen, setIsBatterySelectOpen] = useState(false);
  const [filters, setFilters] = React.useState({
    status: ['Ready', 'Online', 'Idle', 'Busy', 'Working', 'Error', 'Offline'],
    battery: ['Critical', 'Low', 'Good'],
  });

  const videoRefs = useMemo(() => new Map<string, React.RefObject<HTMLVideoElement>>(), []);
  const clientIds = useMemo(
    () => clients.map((client) => client.id).filter(Boolean).join(","),
    [clients]
  );

  const getVideoRef = (clientId: string) => {
    if (!videoRefs.has(clientId)) {
      videoRefs.set(clientId, React.createRef<HTMLVideoElement>());
    }
    return videoRefs.get(clientId)!;
  };

  useEffect(() => {
    let canceled = false;

    const loadDbRobots = async () => {
      try {
        const response = await authedFetch(`${API_BASE}/api/robot/`);
        if (!response.ok) {
          throw new Error(`Failed to load robots (${response.status})`);
        }
        const parsed = RobotListResponseSchema.safeParse(await response.json());
        if (!parsed.success) {
          throw new Error(parsed.error.message);
        }
        if (canceled) {
          return;
        }
        const robots = parsed.data.map((robot) =>
          normalizeClient(robot, { status: 'Offline' })
        );
        setClients((prevClients) => upsertClientsById(robots, prevClients));
      } catch (error) {
        if (!canceled) {
          logError('Failed to load robot roster from database', {
            event: 'dashboard.robots.db_load_failed',
            fields: {
              error: error instanceof Error ? error.message : String(error),
            },
          });
        }
      }
    };

    void loadDbRobots();
    return () => {
      canceled = true;
    };
  }, [authedFetch, logError]);

  useEffect(() => {
    if (!clientIds) {
      return;
    }
    let canceled = false;
    let inFlight = false;
    const updateTelemetry = async () => {
      if (canceled || inFlight) {
        return;
      }
      inFlight = true;
      try {
        const response = await authedFetch(
          `${API_BASE}/api/telemetry/robots?ids=${encodeURIComponent(clientIds)}`
        );
        if (!response.ok) {
          return;
        }
        const parsed = TelemetryStatusListSchema.safeParse(await response.json());
        if (canceled || !parsed.success) {
          return;
        }
        const byId = new Map<string, TelemetryStatus>();
        const byName = new Map<string, TelemetryStatus>();
        for (const entry of parsed.data) {
          if (entry.robot_id) {
            byId.set(entry.robot_id, entry);
          }
          if (entry.robot_name) {
            byName.set(entry.robot_name, entry);
          }
        }
        setClients((prevClients) =>
          prevClients.map((client) => {
            const entry = byId.get(client.id) || byName.get(client.name);
            if (!entry) {
              return client;
            }
            const hasTeleopStatus =
              typeof entry.teleop_status === 'object' && entry.teleop_status !== null;
            const teleopStatus = hasTeleopStatus
              ? (entry.teleop_status as Record<string, unknown>)
              : undefined;
            const readiness = hasTeleopStatus
              ? toBooleanIndicator(teleopStatus?.readiness)
              : client.telemetry.readiness ?? null;
            const availability = hasTeleopStatus
              ? toBooleanIndicator(teleopStatus?.availability)
              : client.telemetry.availability ?? null;
            const readinessComponents = coerceReadinessComponents(
              entry.readiness_components
            );
            const derivedReadiness = deriveAggregateReadiness(readinessComponents);
            let nextStatus = client.status;
            if (hasTeleopStatus && (Boolean(teleopStatus?.teleop_active) || Boolean(teleopStatus?.attempt_active))) {
              nextStatus = 'Busy';
            } else if (hasTeleopStatus && teleopStatus?.ready === true) {
              nextStatus = 'Ready';
            } else if (hasTeleopStatus && teleopStatus?.ready === false) {
              nextStatus = 'Idle';
            }
            return {
              ...client,
              status: nextStatus,
              telemetry: {
                ...client.telemetry,
                teleop_status: hasTeleopStatus ? teleopStatus : client.telemetry?.teleop_status,
                teleop_status_at: entry.teleop_status_at || client.telemetry?.teleop_status_at,
                room_id: entry.room_id || client.telemetry?.room_id || null,
                readiness: readiness ?? derivedReadiness,
                availability: availability ?? derivedReadiness,
                readiness_components: readinessComponents,
              },
            };
          })
        );
      } catch {
        // Ignore telemetry polling errors to avoid UI churn.
      } finally {
        inFlight = false;
      }
    };
    void updateTelemetry();
    const interval = window.setInterval(() => {
      void updateTelemetry();
    }, 1000);
    return () => {
      canceled = true;
      window.clearInterval(interval);
    };
  }, [authedFetch, clientIds]);

  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Filter by search text
      const clientId = client.id?.toLowerCase() || '';
      const clientName = client.name?.toLowerCase() || '';
      if (searchText && !clientId.includes(searchText.toLowerCase()) &&
        !clientName.includes(searchText.toLowerCase())) {
        return false;
      }

      // Filter by status
      if (filters.status.length > 0) {
        const normalizedStatus = client.status?.toLowerCase() || 'online';
        const statusFilters = filters.status.map((status) => status.toLowerCase());
        if (!statusFilters.includes(normalizedStatus)) {
          return false;
        }
      }

      // Filter by battery range
      if (filters.battery.length > 0) {
        const batteryLevel = typeof client.telemetry?.battery === 'number' ? client.telemetry.battery : 0;
        const currentBatteryCategory =
          batteryLevel < 30 ? 'Critical' :
          batteryLevel < 70 ? 'Low' : 'Good';

        // Check if the current battery category is included in the selected filters
        if (!filters.battery.includes(currentBatteryCategory)) {
          return false;
        }
      }

      return true;
    });
  }, [clients, searchText, filters]);

  const onSelect = (type: string, event: React.MouseEvent | React.ChangeEvent, selection: string) => {
    const checked = (event.target as HTMLInputElement).checked;
    setFilters(prev => ({
      ...prev,
      [type]: checked
        ? [...prev[type], selection]
        : prev[type].filter(value => value !== selection)
    }));
  };

  const onDelete = (type: string, id: string) => {
    setFilters(prev => ({
      ...prev,
      [type.toLowerCase()]: prev[type.toLowerCase()].filter(fil => fil !== id)
    }));
  };

  const onDeleteGroup = (type: string) => {
    if (type === 'Battery') {
      setFilters({ battery: [], status: filters.status });
    } else if (type === 'Status') {
      setFilters({ battery: filters.battery, status: [] });
    }
  };

  const onStatusToggle = () => {
    setIsStatusExpanded(!isStatusExpanded);
  };

  const onStatusSelect = (event?: React.MouseEvent<Element, MouseEvent>, value?: string | number) => {
    if (event && typeof value === 'string') {
      onSelect('status', event, value);
    }
  };

  const onBatterySelect = (event?: React.MouseEvent<Element, MouseEvent>, value?: string | number) => {
    if (event && typeof value === 'string') {
      onSelect('battery', event, value);
    }
  };

  const statusMenuItems = (
    <SelectList>
      <SelectOption hasCheckbox key="statusReady" value="Ready" isSelected={filters.status.includes('Ready')}>
        Ready
      </SelectOption>
      <SelectOption hasCheckbox key="statusOnline" value="Online" isSelected={filters.status.includes('Online')}>
        Online
      </SelectOption>
      <SelectOption hasCheckbox key="statusIdle" value="Idle" isSelected={filters.status.includes('Idle')}>
        Idle
      </SelectOption>
      <SelectOption hasCheckbox key="statusBusy" value="Busy" isSelected={filters.status.includes('Busy')}>
        Busy
      </SelectOption>
      <SelectOption hasCheckbox key="statusWorking" value="Working" isSelected={filters.status.includes('Working')}>
        Working
      </SelectOption>
      <SelectOption hasCheckbox key="statusError" value="Error" isSelected={filters.status.includes('Error')}>
        Error
      </SelectOption>
      <SelectOption hasCheckbox key="statusOffline" value="Offline" isSelected={filters.status.includes('Offline')}>
        Offline
      </SelectOption>
    </SelectList>
  );

  // Handle clear all filters
  const onClearAllFilters = () => {
    setSearchText('');
    setFilters({
      status: ['Ready', 'Online', 'Idle', 'Busy', 'Working', 'Error', 'Offline'],
      battery: ['Critical', 'Low', 'Good'],
    });
  };

  const handleOpenModal = (client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const getBatteryColor = (batteryLevel: number) => {
    if (batteryLevel >= 70) return 'green';
    if (batteryLevel >= 30) return 'orange';
    return 'red';
  };

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'online':
      case 'ready':
        return 'green';
      case 'idle':
      case 'standby':
        return 'blue';
      case 'busy':
      case 'working':
        return 'purple';
      case 'error':
      case 'offline':
        return 'red';
      default:
        return 'grey';
    }
  };

  const getTypeColor = (type?: string) => {
    switch ((type || '').toLowerCase()) {
      case 'robot':
        return 'blue';
      case 'operator':
        return 'green';
      default:
        return 'grey';
    }
  };

  return (
    <>
      <PageHeader
        title='Dashboard'
        subtitle='Information about robot fleet'
      />

      <div style={{ padding: '15px' }}>
        <Toolbar id="robots-toolbar" clearAllFilters={onClearAllFilters}>
          <ToolbarContent>
            <ToolbarGroup>
              <ToolbarItem>
                <SearchInput aria-label="Roboto ID"
                  onChange={(_, value) => setSearchText(value)}
                  value={searchText}
                  placeholder="Search by ID"
                />
              </ToolbarItem>
            </ToolbarGroup>

            <ToolbarGroup>
              <ToolbarFilter
                labels={filters.status}
                deleteLabel={(category, label) => onDelete(category as string, label as string)}
                deleteLabelGroup={(category) => onDeleteGroup(category as string)}
                categoryName="Status"
              >
                <Select
                  aria-label="Status"
                  role="menu"
                  toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                    <MenuToggle
                      ref={toggleRef}
                      onClick={onStatusToggle}
                      isExpanded={isStatusExpanded}
                      style={
                        {
                          width: '140px'
                        } as React.CSSProperties
                      }
                    >
                      Status
                      {filters.status.length > 0 && <Badge isRead>{filters.status.length}</Badge>}
                    </MenuToggle>
                  )}
                  onSelect={onStatusSelect}
                  selected={filters.status}
                  isOpen={isStatusExpanded}
                  onOpenChange={(isOpen) => setIsStatusExpanded(isOpen)}
                >
                  {statusMenuItems}
                </Select>
              </ToolbarFilter>
            </ToolbarGroup>
            <ToolbarGroup>
              <ToolbarFilter
                labels={filters.battery}
                deleteLabel={(category, label) => onDelete(category as string, label as string)}
                deleteLabelGroup={(category) => onDeleteGroup(category as string)}
                categoryName="Battery"
              >
                <Select
                  aria-label="Battery"
                  role="menu"
                  toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                    <MenuToggle
                      ref={toggleRef}
                      onClick={() => setIsBatterySelectOpen(!isBatterySelectOpen)}
                      isExpanded={isBatterySelectOpen}
                      style={
                        {
                          width: '140px'
                        } as React.CSSProperties
                      }
                    >
                      Battery
                      {filters.battery.length > 0 && <Badge isRead>{filters.battery.length}</Badge>}
                    </MenuToggle>
                  )}
                  onSelect={onBatterySelect}
                  selected={filters.battery}
                  isOpen={isBatterySelectOpen}
                  onOpenChange={(isOpen) => setIsBatterySelectOpen(isOpen)}
                >
                  <SelectList>
                    <SelectOption hasCheckbox key="Critical" value="Critical" isSelected={filters.battery.includes("Critical")}>Critical</SelectOption>
                    <SelectOption hasCheckbox key="Low" value="Low" isSelected={filters.battery.includes("Low")}>Low</SelectOption>
                    <SelectOption hasCheckbox key="Good" value="Good" isSelected={filters.battery.includes("Good")}>Good</SelectOption>
                  </SelectList>
                </Select>
              </ToolbarFilter>
            </ToolbarGroup>
          </ToolbarContent>
        </Toolbar>
      </div>


      {/* Main container with padding */}
      <div style={{ padding: '15px' }}>
        {/* Use the PatternFly Grid component with 3 columns */}
        <Grid hasGutter>
          {filteredClients.map((client) => {
            const videoRef = getVideoRef(client.id);

            // Each card takes up 4 columns (out of 12) = 1/3 of the screen width
            return (
              <GridItem sm={12} md={6} lg={4} xl={3} key={client.id}>
                <Card>
                  <CardTitle>
                    <Split hasGutter>
                      <SplitItem>{client.name}</SplitItem>
                      {client.type && (
                        <SplitItem>
                          <Label color={getTypeColor(client.type)}>{client.type}</Label>
                        </SplitItem>
                      )}
                    </Split>
                  </CardTitle>
                  <CardBody>
                    <div style={{
                      marginBottom: '10px',
                      paddingTop: '56.25%', /* 16:9 aspect ratio for a more compact look */
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: 'black',
                    }}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          backgroundColor: 'black'
                        }}
                      />
                    </div>
                    <LiveKitViewerProvider
                      videoRef={videoRef}
                      robotId={client.id}
                      roomId={client.telemetry.room_id}
                      activeTrackName={DEFAULT_TRACK}
                    />

                    <Split hasGutter style={{ marginBottom: '15px' }}>
                      <SplitItem>
                        <Label icon={<GlobeIcon />} color="blue">
                          {client.telemetry.ip}
                        </Label>
                      </SplitItem>
                      <SplitItem>
                        <Label
                          icon={<LightbulbIcon />}
                          color={getBatteryColor(client.telemetry.battery)}
                        >
                          {client.telemetry.battery}%
                        </Label>
                      </SplitItem>
                      <SplitItem>
                        <Label
                          icon={<InfoCircleIcon />}
                          color={getStatusColor(client.status)}
                        >
                          {client.status}
                        </Label>
                      </SplitItem>
                    </Split>

                    <Split hasGutter style={{ marginBottom: '15px' }}>
                      <SplitItem>
                        <StatusLight label="Readiness" value={client.telemetry.readiness ?? null} />
                      </SplitItem>
                      <SplitItem>
                        <StatusLight label="Availability" value={client.telemetry.availability ?? null} />
                      </SplitItem>
                    </Split>
                    <Split hasGutter style={{ marginBottom: '15px' }}>
                      <SplitItem>
                        <StatusLight label="Control" value={client.telemetry.readiness_components?.control ?? null} />
                      </SplitItem>
                      <SplitItem>
                        <StatusLight label="Video" value={client.telemetry.readiness_components?.video ?? null} />
                      </SplitItem>
                      <SplitItem>
                        <StatusLight label="Hardware" value={client.telemetry.readiness_components?.hardware ?? null} />
                      </SplitItem>
                    </Split>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                      <Button
                        variant="primary"
                        onClick={() => handleOpenModal(client)}
                      >
                        Control
                      </Button>
                      <Link to={`/bot/${client.id}`}>
                        <Button variant="secondary">Details</Button>
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              </GridItem>
            );
          })}
        </Grid>
      </div>

      {selectedClient && (
        <BotControlModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          client={selectedClient}
          videoRef={getVideoRef(selectedClient.id)}
        />
      )}
    </>
  );
};

export default Dashboard;
